import type {ReactElement} from 'react';
import {openCheckoutForCreditPackPurchase} from '@Modals/checkout-modal/checkout-modal.library';
import type {AiCreditsPillProps, CreditsPillVariant} from './ai-credits-pill.types';
import {CreditsPillSize, CreditsPillTheme} from './ai-credits-pill.types';
import styles from './ai-credits-pill.module.scss';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill-legacy';
import {formatToThousand} from '@Utils/number.util';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {noop} from '@Utils/general.util';
import {isUserPAYG} from '@Libraries/user.library';
import {closeStockMediaPopup} from '@Components/poster-editor/components/poster-add-item-bar/components/stock-media-dropdown/stock-media-popup-controller';

const creditsPillVariantHashMap: Record<CreditsPillTheme, CreditsPillVariant> = {
  [CreditsPillTheme.WARNING]: {
    textColor: 'content-premium',
    pillHoverClass: styles.premium,
  },
  [CreditsPillTheme.DEFAULT]: {
    textColor: 'ct-premium-primary',
    pillHoverClass: '',
  },
  [CreditsPillTheme.ERROR]: {
    textColor: 'content-danger',
    pillHoverClass: styles.error,
  },
};

export function AiCreditsPill({theme, credits, size, showTooltip, tooltipArrowClassName = '', showCreditsCount = true, onPaygUserClick = noop, onClick, ...props}: AiCreditsPillProps): ReactElement {
  const pillSizeClassMap = {
    [CreditsPillSize.SMALL]: PILL_SIZE.XSMALL,
    [CreditsPillSize.MEDIUM]: PILL_SIZE.SMALL,
  };

  const iconSizeMap = {
    [CreditsPillSize.MEDIUM]: IconSize.SIZE_ICON_12,
    [CreditsPillSize.SMALL]: IconSize.SIZE_ICON_16,
  };

  const sizeClassMap = {
    [CreditsPillSize.SMALL]: styles.small,
    [CreditsPillSize.MEDIUM]: styles.medium,
  };

  const onBuyMoreCredits = (): void => {
    if (isUserPAYG()) {
      onPaygUserClick();
      return;
    }

    openCheckoutForCreditPackPurchase({
      headingText: '',
      modalTitle: window.i18next.t('pmwjs_select_a_credit_pack'),
      onSuccess: (): void => {
        void refreshTeamCreditsInStore();
      },
      onOpen: closeStockMediaPopup,
    });
  };

  const handleClick = onClick ?? onBuyMoreCredits;

  const variant = creditsPillVariantHashMap[theme];

  const getTooltipText = (): ReactElement => {
    return <Text size={TextSize.XSMALL} dangerouslySetInnerHTML val={window.i18next.t('pmwjs_you_have_x_credits_purchase', {credit: formatToThousand(credits)})}></Text>;
  };

  const getPillElement = (): ReactElement => {
    return (
      <div className="_position-relative">
        <Pill
          {...props}
          content={{
            type: PILL_CONTENT_TYPE.HYBRID,
            icon: 'icon-flash',
            iconClasses: variant.textColor,
            text: showCreditsCount ? formatToThousand(credits) : '',
            isBold: true,
            textColor: variant.textColor,
            textSize: TextSize.XXSMALL,
            iconSize: iconSizeMap[size],
            gap: showCreditsCount ? 1 : 0,
          }}
          background={PILL_BACKGROUND_COLOR.TRANSPARENT}
          size={pillSizeClassMap[size]}
          className={`${styles.creditsPillContainer} ${sizeClassMap[size]} ${showCreditsCount ? '' : styles.iconOnly} ${variant.pillHoverClass} radius-12 ${props.className ?? ''}`}
          onClick={handleClick}
        />
      </div>
    );
  };

  if (showTooltip) {
    return (
      <OverlayTrigger
        trigger={['hover', 'focus', 'click']}
        overlay={<Tooltip className={`_rounded-arrow ${tooltipArrowClassName}`}>{getTooltipText()}</Tooltip>}
        delay={{show: 100, hide: 100}}
        placement="bottom"
      >
        {getPillElement()}
      </OverlayTrigger>
    );
  }

  return getPillElement();
}
